import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Search, X, Plus, Check, Loader2, ExternalLink, GraduationCap, BookOpen, Bookmark, StickyNote } from "lucide-react";
import { cn } from "@/lib/utils";
import { SpeakButton } from "@/components/common/SpeakButton";
import {
    useDictionaryStore,
    type DictionaryEntry,
    type DictionaryLanguage,
} from "@/stores/dictionary";
import { useSavedVocabulary, useSavedVocabularyWords, useVocabularySaveStatus } from "@/hooks/use-saved-vocabulary";
import { WordNotes } from "@/components/vocabulary/WordNotes";
import { useToast } from "@/hooks/use-toast";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { useAIAssistant } from "@/contexts/AIAssistantContext";

interface DictionaryPanelProps {
    onClose?: () => void;
    isMobile?: boolean;
}

type DictionaryTab = "definition" | "saved" | "notes";

/** Long enough to coalesce a typed word, short enough to feel immediate. */
const SEARCH_DEBOUNCE_MS = 300;

export function DictionaryPanel({ onClose, isMobile }: DictionaryPanelProps) {
    const [activeTab, setActiveTab] = useState<DictionaryTab>("definition");
    const {
        searchValue,
        setSearchValue,
        entry,
        isLoading,
        error,
        language,
        setLanguage,
        fetchDefinition,
        clearEntry,
    } = useDictionaryStore();
    const inputRef = useRef<HTMLInputElement>(null);

    // One request per pause in typing. Without this, every keystroke starts a
    // lookup and cancels the last one, which burns a request per character on a
    // word someone is still typing.
    const { debounced: runLookup, cancel: cancelLookup } = useDebouncedCallback(
        (value: string) => fetchDefinition(value),
        SEARCH_DEBOUNCE_MS,
    );

    useEffect(() => {
        if (activeTab === "definition") {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [activeTab]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchValue(value);

        if (!value.trim()) {
            cancelLookup();
            clearEntry();
            return;
        }

        runLookup(value);
    }, [runLookup, cancelLookup, clearEntry, setSearchValue]);

    const handleLanguageChange = useCallback((value: string) => {
        if (value === "en" || value === "vi") {
            const newLang = value as DictionaryLanguage;
            setLanguage(newLang);
            if (searchValue.trim()) {
                fetchDefinition(searchValue, newLang);
            }
        }
    }, [setLanguage, searchValue, fetchDefinition]);

    const handleClear = useCallback(() => {
        // Drop any lookup still waiting on the debounce, or it would land after
        // the field is already empty and repopulate the panel.
        cancelLookup();
        setSearchValue("");
        clearEntry();
        inputRef.current?.focus();
    }, [cancelLookup, clearEntry, setSearchValue]);

    const handleSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!searchValue.trim()) return;
        // Submitting mid-typing would otherwise also let the pending debounce
        // fire a second, redundant lookup for the same word.
        cancelLookup();
        fetchDefinition(searchValue);
    }, [searchValue, cancelLookup, fetchDefinition]);

    const tabs = [
        { id: "definition" as const, label: "Definition", icon: BookOpen },
        { id: "saved" as const, label: "Saved Words", icon: Bookmark },
        { id: "notes" as const, label: "Notes", icon: StickyNote },
    ];

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-2.5 md:p-3 border-b border-border bg-card flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className={cn(
                                "shrink-0",
                                isMobile ? "min-h-[44px] min-w-[44px]" : "h-7 w-7"
                            )}
                            aria-label="Close dictionary"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                    <div className="h-7 w-7 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
                        <BookOpen className="h-4 w-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm truncate">Dictionary</h3>
                </div>

                <ToggleGroup
                    type="single"
                    value={language}
                    onValueChange={handleLanguageChange}
                    className="bg-muted/50 rounded-full p-0.5"
                >
                    <ToggleGroupItem
                        value="en"
                        className="rounded-full px-2.5 py-0.5 text-[11px] data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:text-foreground"
                    >
                        EN
                    </ToggleGroupItem>
                    <ToggleGroupItem
                        value="vi"
                        className="rounded-full px-2.5 py-0.5 text-[11px] data-[state=on]:bg-background data-[state=on]:shadow-sm data-[state=on]:text-foreground"
                    >
                        VI
                    </ToggleGroupItem>
                </ToggleGroup>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-border/50 px-2 flex-shrink-0">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={cn(
                                "flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors relative",
                                isActive
                                    ? "text-primary"
                                    : "text-muted-foreground hover:text-foreground"
                            )}
                        >
                            <Icon className="h-3.5 w-3.5" />
                            <span>{tab.label}</span>
                            {isActive && (
                                <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Search (only for definition tab) */}
            {activeTab === "definition" && (
                <div className="p-2.5 border-b border-border/50">
                    <form onSubmit={handleSubmit} className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            ref={inputRef}
                            value={searchValue}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={language === "en" ? "Search a word..." : "Tìm kiếm..."}
                            className="pl-9 pr-9 h-9 text-sm rounded-xl border-2 focus-visible:ring-primary/20"
                        />
                        {searchValue && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 rounded-full"
                                onClick={handleClear}
                                aria-label="Clear search"
                            >
                                <X className="h-3 w-3" />
                            </Button>
                        )}
                    </form>
                </div>
            )}

            {/* Content */}
            <ScrollArea className="flex-1 min-h-0">
                {activeTab === "definition" && (
                    <div className="p-3">
                        {isLoading ? (
                            <DefinitionSkeleton />
                        ) : error ? (
                            <div className="text-center py-6">
                                <div className="bg-destructive/10 rounded-xl p-4">
                                    <p className="text-sm text-destructive">{error}</p>
                                </div>
                                {language === "vi" && (
                                    <a
                                        href={`https://vtudien.com/anh-viet/dictionary/nghia-cua-tu-${encodeURIComponent(searchValue)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                        VTudien
                                    </a>
                                )}
                            </div>
                        ) : entry ? (
                            <DefinitionContent entry={entry} language={language} isMobile={isMobile} />
                        ) : (
                            <EmptyState language={language} isMobile={isMobile} />
                        )}

                        {/* Notes for the searched word — shown regardless of whether the
                            external definition lookup succeeds, so the user always sees
                            notes they took on this word. Renders nothing when there are none. */}
                        {!isLoading && (entry?.word || searchValue.trim()) && (
                            <WordNotes
                                word={entry?.word ?? searchValue}
                                variant="inline"
                                className="mt-4 pt-4 border-t border-border/40"
                            />
                        )}
                    </div>
                )}

                {activeTab === "saved" && (
                    <SavedWordsTab />
                )}

                {activeTab === "notes" && (
                    <div className="p-2">
                        <WordNotes variant="list" />
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

function DefinitionSkeleton() {
    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Skeleton className="h-7 w-28 rounded-xl" />
                <Skeleton className="h-8 w-8 rounded-xl" />
            </div>
            <Skeleton className="h-4 w-24 rounded-lg" />
            <div className="space-y-2.5">
                <div className="bg-primary/5 rounded-xl p-3 space-y-2">
                    <Skeleton className="h-3.5 w-full rounded" />
                    <Skeleton className="h-3.5 w-3/4 rounded" />
                </div>
                <div className="bg-primary/5 rounded-xl p-3 space-y-2">
                    <Skeleton className="h-3.5 w-5/6 rounded" />
                    <Skeleton className="h-3.5 w-2/3 rounded" />
                </div>
            </div>
        </div>
    );
}

interface DefinitionContentProps {
    entry: DictionaryEntry;
    language: DictionaryLanguage;
    isMobile?: boolean;
}

function DefinitionContent({ entry, language, isMobile }: DefinitionContentProps) {
    const { saveVocabulary, isSaving } = useSavedVocabulary();
    const { isSaved, refresh: refreshSaveStatus } = useVocabularySaveStatus(entry.word);
    const { toast } = useToast();
    const { openWidget: openAIWidget, sendMessage } = useAIAssistant();

    useEffect(() => {
        refreshSaveStatus();
    }, [entry.word, refreshSaveStatus]);

    const handleSaveVocabulary = useCallback(async () => {
        if (!entry) return;

        const firstMeaning = entry.meanings[0];
        const firstDefinition = firstMeaning?.definitions[0];

        try {
            await saveVocabulary({
                word: entry.word,
                definition: firstDefinition?.definition || "",
                example: firstDefinition?.example || "",
                ipa: entry.phonetic || entry.phonetics.find(p => p.text)?.text,
                type: firstMeaning?.partOfSpeech,
            });

            toast({
                title: "Word saved!",
                description: `"${entry.word}" has been added to your vocabulary.`,
            });

            refreshSaveStatus();
        } catch (err) {
            toast({
                title: "Failed to save",
                description: err instanceof Error ? err.message : "Could not save vocabulary",
                variant: "destructive",
            });
        }
    }, [entry, saveVocabulary, toast, refreshSaveStatus]);

    const handleAskAI = useCallback(() => {
        if (!entry) return;
        openAIWidget();
        sendMessage(`Explain the word "${entry.word}" in detail. Include its meaning, common usages, example sentences, and any synonyms or related words.`);
    }, [entry, openAIWidget, sendMessage]);

    return (
        <div className="space-y-3">
            {/* Word Header */}
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="font-bold text-lg">{entry.word}</h3>
                    {entry.phonetic && (
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">
                            {entry.phonetic}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <SpeakButton
                        text={entry.word}
                        rate={0.9}
                        className="shrink-0 h-8 w-8 p-0 rounded-lg hover:bg-primary/5"
                        iconClassName="h-4 w-4"
                        ariaLabel="Play pronunciation"
                    />
                    {language === "en" && (
                        <Button
                            variant={isSaved ? "secondary" : "default"}
                            size="sm"
                            className="gap-1 rounded-lg text-xs h-8"
                            onClick={handleSaveVocabulary}
                            disabled={isSaving || isSaved}
                        >
                            {isSaving ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : isSaved ? (
                                <Check className="h-3.5 w-3.5" />
                            ) : (
                                <Plus className="h-3.5 w-3.5" />
                            )}
                            {isSaved ? "Saved" : "Save"}
                        </Button>
                    )}
                </div>
            </div>

            {/* Meanings */}
            <div className="space-y-2.5">
                {entry.meanings.map((meaning, idx) => (
                    <div key={idx} className="bg-primary/5 rounded-xl p-3 space-y-2">
                        <Badge variant="secondary" className="text-[11px] font-medium px-2 py-0.5 rounded-md">
                            {meaning.partOfSpeech}
                        </Badge>
                        <ul className="text-sm space-y-2 leading-relaxed">
                            {meaning.definitions.slice(0, 5).map((def, defIdx) => (
                                <li key={defIdx} className="text-foreground">
                                    {def.definition}
                                    {def.example && (
                                        <div className="mt-1.5 pl-3 border-l-2 border-primary/20 bg-background/60 rounded-r-lg py-1.5 px-2.5">
                                            <p className="text-xs text-muted-foreground italic">
                                                "{def.example}"
                                            </p>
                                        </div>
                                    )}
                                    {def.synonyms && def.synonyms.length > 0 && (
                                        <p className="text-xs mt-1.5 text-muted-foreground">
                                            <span className="font-medium text-foreground/70">Synonyms:</span>{" "}
                                            {def.synonyms.slice(0, 5).join(", ")}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {/* Resources */}
            <div className="border-t border-border/50 pt-3 space-y-2">
                <p className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">Resources</p>
                <div className="flex flex-wrap gap-1.5">
                    <button
                        type="button"
                        onClick={handleAskAI}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                    >
                        <GraduationCap className="h-3.5 w-3.5" />
                        Ask Coach
                    </button>
                    {language === "en" ? (
                        <>
                            <a
                                href={`https://www.oxfordlearnersdictionaries.com/definition/english/${encodeURIComponent(entry.word)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors"
                            >
                                <img loading="lazy" decoding="async"
                                    src="https://www.google.com/s2/favicons?domain=oxfordlearnersdictionaries.com&sz=32"
                                    alt=""
                                    className="h-3.5 w-3.5 rounded-sm"
                                />
                                Oxford
                            </a>
                            <a
                                href={`https://dictionary.cambridge.org/dictionary/english/${encodeURIComponent(entry.word)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-colors"
                            >
                                <img loading="lazy" decoding="async"
                                    src="https://www.google.com/s2/favicons?domain=cambridge.org&sz=32"
                                    alt=""
                                    className="h-3.5 w-3.5 rounded-sm"
                                />
                                Cambridge
                            </a>
                        </>
                    ) : (
                        <a
                            href={`https://vtudien.com/anh-viet/dictionary/nghia-cua-tu-${encodeURIComponent(entry.word)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors"
                        >
                            <ExternalLink className="h-3 w-3" />
                            VTudien
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

interface EmptyStateProps {
    language: DictionaryLanguage;
    isMobile?: boolean;
}

function EmptyState({ language, isMobile }: EmptyStateProps) {
    return (
        <div className={cn("flex flex-col items-center justify-center text-center", isMobile ? "py-10" : "py-14")}>
            <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-3">
                <BookOpen className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-bold text-sm mb-1">
                {language === "en" ? "Build your vocabulary" : "Tra cứu từ điển"}
            </h3>
            <p className="text-xs text-muted-foreground max-w-[200px] leading-relaxed">
                {language === "en"
                    ? "Search for a word to see its definition, pronunciation, and examples"
                    : "Nhập từ cần tìm vào ô tìm kiếm phía trên"}
            </p>
        </div>
    );
}

function SavedWordsTab() {
    // This tab renders a word, its definition and a type badge — nothing else.
    // It asks for exactly those four columns instead of the whole deck (741 B/row
    // vs ~90 B/row measured) and pages them, instead of pulling every row at once.
    const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useSavedVocabularyWords();
    const vocabulary = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
    const total = data?.pages[0]?.total ?? 0;

    if (isLoading) {
        return (
            <div className="p-3 space-y-2">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5">
                        <Skeleton className="h-8 w-8 rounded-lg" />
                        <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-4 w-20 rounded" />
                            <Skeleton className="h-3 w-32 rounded" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (!vocabulary || vocabulary.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-14">
                <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-3">
                    <Bookmark className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-bold text-sm mb-1">No saved words yet</h3>
                <p className="text-xs text-muted-foreground max-w-[200px] leading-relaxed">
                    Save words from definitions to review them later
                </p>
            </div>
        );
    }

    return (
        <div className="p-2">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2">
                {total} saved word{total !== 1 ? "s" : ""}
            </p>
            <div className="space-y-0.5">
                {vocabulary.map((word) => (
                    <div
                        key={word.id}
                        className="flex items-center gap-3 px-2.5 py-2 rounded-lg hover:bg-primary/5 transition-colors group"
                    >
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="text-sm font-medium truncate" title={word.word}>{word.word}</p>
                            {word.definition && (
                                <p className="text-xs text-muted-foreground line-clamp-2 break-words" title={word.definition}>{word.definition}</p>
                            )}
                        </div>
                        {word.type && (
                            <Badge variant="secondary" className="text-[11px] px-1.5 py-0.5 shrink-0 rounded-md">
                                {word.type}
                            </Badge>
                        )}
                    </div>
                ))}
            </div>
            {hasNextPage && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-1 text-xs text-muted-foreground"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                >
                    {isFetchingNextPage ? "Loading…" : "Show more"}
                </Button>
            )}
        </div>
    );
}

